import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import {
  useChangeForceLogoutMutation,
  useGetDevicesQuery,
  useDeleteDeviceMutation,
} from "../../services";

const DeviceList: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(1);
  const limit = 15;
  const [allDevices, setAllDevices] = useState<any[]>([]);

  // Fetch devices from RTK Query with pagination
  const {
    data: devices,
    isLoading,
    isFetching,
    error,
  } = useGetDevicesQuery({ page, limit });
  const [changeForceLogout] = useChangeForceLogoutMutation();
  const [deleteDevice] = useDeleteDeviceMutation();
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [logoutTarget, setLogoutTarget] = useState<any | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const closeModals = () => {
    setDeleteTarget(null);
    setLogoutTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsActionLoading(true);
    try {
      await deleteDevice({ deviceid: deleteTarget._id }).unwrap();
      // Update local state for immediate reflect without refresh
      setAllDevices((prev) =>
        prev.filter((d) => d._id !== deleteTarget._id)
      );
      closeModals();
    } catch (err) {
      console.error("Failed to delete device:", err);
      alert("Failed to delete device.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const confirmLogout = async () => {
    if (!logoutTarget) return;
    setIsActionLoading(true);
    try {
      await changeForceLogout({ deviceid: logoutTarget._id }).unwrap();
      // Update local state for immediate reflect
      setAllDevices((prev) =>
        prev.map((d) =>
          d._id === logoutTarget._id ? { ...d, status: "Force_Logout" } : d
        )
      );
      closeModals();
    } catch (err) {
      console.error("Failed to sync device:", err);
      alert("Failed to sync device.");
    } finally {
      setIsActionLoading(false);
    }
  };
  // Append new page data to allDevices
  React.useEffect(() => {
    if (devices && Array.isArray(devices)) {
      setAllDevices((prev) => (page === 1 ? devices : [...prev, ...devices]));
    }
  }, [devices, page]);

  // Infinite scroll: call next page when reaching bottom
  React.useEffect(() => {
    const container = document.getElementById("device-table-scroll");
    if (!container) return;
    const handleScroll = () => {
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 40
      ) {
        // Only fetch next page if not already loading and more data is available
        if (!isFetching && devices && devices.length === limit) {
          setPage((prev) => prev + 1);
        }
      }
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [devices, isFetching, limit]);

  // Helper to format date as DD/MM/YY
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  // THEME CLASSES
  const pageBg = theme === "dark" ? "bg-[#0A0A0A]" : "bg-gray-50";
  const heading = "text-text";
  const crumbLink = theme === "dark" ? "text-gray-500" : "text-gray-500";
  const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";

  const cardClass =
    theme === "dark"
      ? "bg-[#1A1A1A] border-0 text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const cardHeader =
    theme === "dark"
      ? "px-6 py-4 border-b border-[#2A2A2A] text-white flex items-center justify-between"
      : "px-6 py-4 border-b border-gray-200 text-gray-800 flex items-center justify-between";

  const thClass =
    theme === "dark"
      ? "px-6 py-3 text-left text-sm font-boldgr text-[#F3F3F6]  tracking-wider border-b border-[#2A2A2A]"
      : "px-6 py-3 text-left text-sm font-bold text-gray-700  tracking-wider border-b border-gray-200";

  const tdClass =
    theme === "dark"
      ? "px-6 py-4 text-sm text-text-table border-b border-[#2A2A2A]"
      : "px-6 py-4 text-sm text-text-table border-b border-gray-200";

  const iconHover = theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5";
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );

  const sidebarWidth = isSidebarOpen ? 256 : 64;
  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-6">
        <h1 className={`text-lg font-medium ${heading} mb-2`}>
          Session Overview
        </h1>
        <div className="text-sm flex items-center gap-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M3 11.9896V14.5C3 17.7998 3 19.4497 4.02513 20.4749C5.05025 21.5 6.70017 21.5 10 21.5H14C17.2998 21.5 18.9497 21.5 19.9749 20.4749C21 19.4497 21 17.7998 21 14.5V11.9896C21 10.3083 21 9.46773 20.6441 8.74005C20.2882 8.01237 19.6247 7.49628 18.2976 6.46411L16.2976 4.90855C14.2331 3.30285 13.2009 2.5 12 2.5C10.7991 2.5 9.76689 3.30285 7.70242 4.90855L5.70241 6.46411C4.37533 7.49628 3.71179 8.01237 3.3559 8.74005C3 9.46773 3 10.3083 3 11.9896Z"
              stroke="#ADADAD"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 21.5V16.5C15 15.0858 15 14.3787 14.5607 13.9393C14.1213 13.5 13.4142 13.5 12 13.5C10.5858 13.5 9.87868 13.5 9.43934 13.9393C9 14.3787 9 15.0858 9 16.5V21.5"
              stroke="#ADADAD"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Link
            to="/"
            className={`${crumbLink} hover:text-[#B6CB24] transition-colors`}
          >
            Dashboard
          </Link>
          <span className={crumbSep}>&gt;</span>
          <span className="text-[#B6CB24] font-normal">All Devices</span>
        </div>
      </div>

      {/* Card */}
      <div className={`${cardClass} rounded-lg overflow-hidden shadow-lg`}>
        <div className={cardHeader}>
          <span className="text-base font-medium">All Devices Details</span>
          {/* Replace Add Device button with filter dropdown styled as per image */}
          <div className="relative">
            <select
              className="appearance-none w-44 px-4 py-2 rounded-md border border-[#B6CB24] bg-transparent text-sm font-medium focus:outline-none transition-colors"
              style={{
                color: theme === "dark" ? "#F3F3F6" : "#232323",
                background: theme === "dark" ? "#181818" : "#fff",
              }}
              defaultValue=""
            >
              <option value="" disabled>
                Filter by list
              </option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
            </select>

            {/* Dropdown arrow */}
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: "8px",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="26"
                viewBox="0 0 26 26"
                fill="none"
              >
                <path
                  d="M6.48047 9.72124L12.9612 16.2019L19.442 9.72119"
                  stroke="#F3F3F6"
                  stroke-width="1.5"
                  stroke-miterlimit="16"
                />
              </svg>
            </span>
          </div>
        </div>

        {/* Infinite scroll table with sticky header */}
        <div
          className="relative"
          style={{
            maxHeight: "500px",
            overflowY: "auto",
          }}
          id="device-table-scroll"
        >
          <table className="min-w-full">
            <thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                background: theme === "dark" ? "#1A1A1A" : "#fff",
              }}
            >
              <tr>
                <th className={thClass}>Device Id</th>
                <th className={thClass}>Device Name</th>
                <th className={thClass}>Gym Name</th>
                <th className={thClass}>Screen Type</th>
                <th className={thClass}>Last Synced</th>
                <th className={thClass}>Last Used</th>
                <th className={thClass}>Status</th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody>
              {/* Infinite scroll logic */}
              {isLoading || (isFetching && page === 1) ? (
                <tr>
                  <td className={tdClass} colSpan={8}>
                    Loading devices...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td className={tdClass} colSpan={8}>
                    Failed to load devices.
                  </td>
                </tr>
              ) : !allDevices || allDevices.length === 0 ? (
                <tr>
                  <td className={tdClass} colSpan={8}>
                    No devices found.create a new device to get started.
                  </td>
                </tr>
              ) : (
                <>
                  {allDevices.map((row) => (
                    <tr key={row.id}>
                      <td className={tdClass}>{row.code}</td>
                      <td className={tdClass}>{row.name}</td>
                      <td className={tdClass}>{row.gym?.name}</td>
                      <td className={tdClass}>
                        <span className="flex items-center gap-2">
                          {row.type === "Premium" ? (
                            <span className="inline-flex items-center justify-center">
                              {/* Premium SVG */}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  fill="#B6CB24"
                                  fillOpacity="0.1"
                                />
                                <path
                                  d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z"
                                  fill="#B6CB24"
                                />
                              </svg>
                            </span>
                          ) : (
                            <span className="inline-flex items-center justify-center">
                              {/* Basic SVG */}
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="#B6CB24"
                                  strokeWidth="2"
                                  fill="none"
                                />
                              </svg>
                            </span>
                          )}
                          <span
                            className={`text-xs font-semibold ${row.type === "Premium"
                              ? "text-[#B6CB24]"
                              : "text-gray-400"
                              }`}
                          >
                            {row.type}
                          </span>
                        </span>
                      </td>
                      <td className={tdClass}>{formatDate(row.lastSync)}</td>
                      <td className={tdClass}>{formatDate(row.lastUsed)}</td>
                      <td className={tdClass}>{row.status}</td>
                      <td className={tdClass}>
                        <div className="flex items-center gap-2">
                          {row.status === "Force_Logout" ? (
                            <button
                              title="Delete"
                              className={`p-1.5 rounded transition-colors ${iconHover}`}
                              onClick={() => setDeleteTarget(row)}
                            >
                              {/* Trash/Delete SVG icon */}
                             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M12.0342 1.25C12.5408 1.25 12.9701 1.24949 13.3252 1.28125C13.694 1.31425 14.038 1.38536 14.3721 1.55859C14.5041 1.62709 14.6306 1.7068 14.75 1.7959C15.0518 2.02101 15.2663 2.29951 15.457 2.61719C15.6405 2.92284 15.8269 3.3089 16.0479 3.76465L16.5254 4.75H21C21.4142 4.75 21.75 5.08579 21.75 5.5C21.75 5.91421 21.4142 6.25 21 6.25H20.2051L19.6289 15.5713C19.5506 16.8374 19.4891 17.8405 19.3623 18.6396C19.2336 19.4506 19.0281 20.126 18.6162 20.7168C18.2393 21.2572 17.7534 21.7138 17.1904 22.0566C16.5754 22.4312 15.8887 22.5955 15.0713 22.6738C14.2659 22.751 13.2615 22.75 11.9932 22.75C10.723 22.75 9.71658 22.7512 8.91016 22.6738C8.09168 22.5953 7.40366 22.4311 6.78809 22.0557C6.22491 21.7122 5.74005 21.2543 5.36328 20.7129C4.9515 20.1211 4.74587 19.4449 4.61816 18.6328C4.49235 17.8326 4.43298 16.8283 4.35645 15.5605L3.79395 6.25H3C2.58579 6.25 2.25 5.91421 2.25 5.5C2.25 5.08579 2.58579 4.75 3 4.75H7.57031L7.97656 3.86035C8.19193 3.38787 8.37299 2.98768 8.55371 2.6709C8.7415 2.34175 8.95531 2.05271 9.26074 1.81836C9.38127 1.7259 9.50945 1.64359 9.64355 1.57227L9.77148 1.50879C10.0717 1.37296 10.3826 1.3123 10.7129 1.28223C11.076 1.24919 11.5151 1.25 12.0342 1.25ZM5.29688 6.25L5.85352 15.4697C5.93181 16.7666 5.98757 17.6867 6.09961 18.3994C6.20977 19.1001 6.36482 19.5261 6.59473 19.8564C6.85249 20.2268 7.18424 20.5395 7.56934 20.7744C7.91297 20.984 8.34764 21.1129 9.05371 21.1807C9.77189 21.2496 10.6939 21.25 11.9932 21.25C13.2905 21.25 14.2106 21.2494 14.9277 21.1807C15.6328 21.1131 16.0668 20.9845 16.4102 20.7754C16.7469 20.5703 17.0435 20.3058 17.2852 19.9951L17.3857 19.8584C17.6156 19.5286 17.7699 19.1036 17.8809 18.4043C17.9937 17.6926 18.0517 16.7737 18.1318 15.4785L18.7021 6.25H5.29688ZM12.0342 2.75C11.4881 2.75 11.1282 2.75092 10.8486 2.77637C10.5832 2.80053 10.448 2.84313 10.3477 2.89648C10.2868 2.92885 10.2285 2.96588 10.1738 3.00781C10.0837 3.07697 9.98855 3.18253 9.85645 3.41406C9.71731 3.65796 9.56738 3.9854 9.34082 4.48242L9.21875 4.75H14.8584L14.6982 4.41895C14.4657 3.93934 14.312 3.62365 14.1709 3.38867C14.0372 3.16597 13.9427 3.06462 13.8535 2.99805C13.8264 2.97784 13.7982 2.95839 13.7695 2.94043L13.6816 2.89062C13.5828 2.83935 13.4504 2.79856 13.1914 2.77539C12.9184 2.75098 12.5672 2.75 12.0342 2.75Z" fill="url(#paint0_linear_1768_7213)"/>
  <path d="M9.5 16.5V10.5" stroke="url(#paint1_linear_1768_7213)" stroke-width="1.5" stroke-linecap="round"/>
  <path d="M14.5 16.5V10.5" stroke="url(#paint2_linear_1768_7213)" stroke-width="1.5" stroke-linecap="round"/>
  <defs>
    <linearGradient id="paint0_linear_1768_7213" x1="12" y1="1.25" x2="12" y2="22.75" gradientUnits="userSpaceOnUse">
      <stop stop-color="#D92626"/>
      <stop offset="1" stop-color="#AD1F1F"/>
    </linearGradient>
    <linearGradient id="paint1_linear_1768_7213" x1="10" y1="10.5" x2="10" y2="16.5" gradientUnits="userSpaceOnUse">
      <stop stop-color="#D92626"/>
      <stop offset="1" stop-color="#AD1F1F"/>
    </linearGradient>
    <linearGradient id="paint2_linear_1768_7213" x1="15" y1="10.5" x2="15" y2="16.5" gradientUnits="userSpaceOnUse">
      <stop stop-color="#D92626"/>
      <stop offset="1" stop-color="#AD1F1F"/>
    </linearGradient>
  </defs>
</svg>
                            </button>
                          ) : (
                            <button
                              title="Sync"
                              className={`p-1.5 rounded transition-colors ${iconHover}`}
                              onClick={() => setLogoutTarget(row)}
                            >
                              {/* Sync SVG icon (existing) */}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M7.24996 2.24984C7.80225 2.24984 8.24996 2.69755 8.24996 3.24984C8.24996 3.80212 7.80225 4.24984 7.24996 4.24984C6.25562 4.24984 5.95466 4.25864 5.73239 4.3182C5.0422 4.50313 4.50326 5.04207 4.31832 5.73226C4.25876 5.95454 4.24996 6.25549 4.24996 7.24984V16.7498L4.25192 17.3748C4.25712 17.8897 4.27364 18.1007 4.31832 18.2674C4.50326 18.9576 5.0422 19.4966 5.73238 19.6815C5.95466 19.741 6.25561 19.7498 7.24996 19.7498C7.80225 19.7498 8.24996 20.1976 8.24996 20.7498C8.24996 21.3021 7.80225 21.7498 7.24996 21.7498C6.38438 21.7498 5.75553 21.758 5.21481 21.6131C3.83444 21.2433 2.75655 20.1654 2.38668 18.785C2.2418 18.2443 2.24996 17.6154 2.24996 16.7498V7.24984C2.24996 6.38425 2.2418 5.7554 2.38668 5.21468C2.75655 3.83431 3.83444 2.75642 5.21481 2.38656C5.75553 2.24167 6.38438 2.24984 7.24996 2.24984Z"
                                  fill="#F3F3F6"
                                />
                                <path
                                  d="M8.25 10.9999C7.69772 10.9999 7.25 11.4476 7.25 11.9999C7.25002 12.5521 7.69773 12.9999 8.25 12.9999H18.8594C18.5543 13.304 18.206 13.6257 17.8447 13.9393C17.2998 14.4125 16.7514 14.852 16.3379 15.1747C16.1317 15.3356 15.9603 15.4673 15.8408 15.5575C15.7811 15.6026 15.7345 15.6376 15.7031 15.661C15.6877 15.6725 15.6757 15.6807 15.668 15.6864C15.6641 15.6893 15.661 15.6919 15.6592 15.6932L15.6572 15.6942C15.2125 16.0217 15.1178 16.6479 15.4453 17.0926C15.7727 17.5371 16.3981 17.6327 16.8428 17.3055L16.0996 16.2958C16.1449 16.3573 16.1949 16.4251 16.25 16.4999C16.7938 17.2382 16.8389 17.3005 16.8428 17.3055L16.8447 17.3036L16.8477 17.3016C16.8502 17.2998 16.8539 17.2971 16.8584 17.2938C16.8677 17.287 16.8813 17.2773 16.8984 17.2645C16.933 17.2388 16.9827 17.201 17.0459 17.1532C17.1725 17.0575 17.3527 16.9201 17.5684 16.7518C17.9985 16.4161 18.5755 15.9535 19.1553 15.4501C19.7303 14.9508 20.331 14.3912 20.7959 13.8651C21.0273 13.6032 21.2466 13.3258 21.4131 13.0497C21.563 12.801 21.75 12.4265 21.75 11.9999C21.75 11.5733 21.563 11.1988 21.4131 10.9501C21.2466 10.6739 21.0273 10.3966 20.7959 10.1346C20.331 9.6085 19.7303 9.0489 19.1553 8.54966C18.5754 8.04626 17.9985 7.5836 17.5684 7.24791C17.3527 7.07961 17.1725 6.94218 17.0459 6.84654C16.9827 6.79878 16.933 6.76095 16.8984 6.73521C16.8813 6.72243 16.8677 6.71278 16.8584 6.70591C16.8539 6.70261 16.8502 6.69996 16.8477 6.6981L16.8447 6.69615L16.8438 6.69517C16.84 6.70007 16.7944 6.76065 16.25 7.49986C16.1949 7.57463 16.1449 7.64243 16.0996 7.70396L16.8428 6.6942C16.3981 6.36702 15.7718 6.46251 15.4443 6.90709C15.1172 7.35176 15.2127 7.97811 15.6572 8.30552L15.6592 8.3065C15.661 8.30783 15.6641 8.31046 15.668 8.31334C15.6757 8.31907 15.6877 8.32727 15.7031 8.33873C15.7345 8.36209 15.7811 8.39716 15.8408 8.44224C15.9603 8.53247 16.1317 8.66412 16.3379 8.82506C16.7514 9.14773 17.2998 9.58728 17.8447 10.0604C18.206 10.3741 18.5543 10.6958 18.8594 10.9999H8.25Z"
                                  fill="#C6D92C"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {isFetching && (
                    <tr>
                      <td className={tdClass} colSpan={8}>
                        <div className="text-center py-2 text-xs text-gray-400">
                          Loading more...
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme === "dark" ? "bg-black/60" : "bg-black/40"
            }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${theme === "dark"
                ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
                : "bg-white text-gray-900 border-gray-200"
              }`}
          >
            <button
              onClick={closeModals}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
                }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>{" "}
            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M19.5 5.5L19.0982 12.0062M4.5 5.5L5.10461 15.5248C5.25945 18.0922 5.33688 19.3759 5.97868 20.299C6.296 20.7554 6.7048 21.1407 7.17905 21.4302C7.85035 21.84 8.68108 21.9631 10 22"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M20 15L13 21.9995M20 22L13 15.0005"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Delete Device</h3>
              <p
                className={`${theme === "dark" ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
              >
                Are you sure you want to delete "{deleteTarget.name}"?
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-[120px] h-[40px] font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid #D92C2C",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                  }}
                  disabled={isActionLoading}
                >
                  {isActionLoading ? "Deleting..." : "Delete"}
                </button>
                <button
                  type="button"
                  onClick={closeModals}
                  className="w-[120px] h-[40px] font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isActionLoading}
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {logoutTarget && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme === "dark" ? "bg-black/60" : "bg-black/40"
            }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${theme === "dark"
                ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
                : "bg-white text-gray-900 border-gray-200"
              }`}
          >
            <button
              onClick={closeModals}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
                }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>{" "}
            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                  }`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#B6CB24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Force Logout</h3>
              <p
                className={`${theme === "dark" ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
              >
                Are you sure you want to force logout "{logoutTarget.name}"?
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={confirmLogout}
                  className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid #D92C2C",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                  }}
                  disabled={isActionLoading}
                >
                  {isActionLoading ? "Logging out..." : "Confirm logout"}
                </button>
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 font-medium hover:opacity-90 text-black rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isActionLoading}
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceList;
